/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, CSSProperties} from 'react';
import {makeStyles} from '@material-ui/core';
import isObject from 'lodash.isobject';
import {DLSTheme} from '../../types/types';
import CheckBox, {CheckboxTypes} from '../checkbox/CheckBox';
import {FormItem} from './FormItem';

// defines the FormCheckBox component prop type definitions ...
export interface FormCheckBoxProps extends CheckboxTypes {
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  name?: string;
  disabled?: boolean;
  useWrapperStyle?: boolean;
  formItemStyle?: CSSProperties;
};

// defines the default component styles ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  checkboxWrapper : {
    marginTop   : 38,
    paddingLeft : 10
  }
}));

/**
 * @FormCheckBox
 *
 * The FormCheckBox component provides a generalized DLS component which is typically used
 * within the various Entity Editors for displaying and setting an entities inheritable
 * value.
 *
 * ```typescript
 *
 *    import {FormCheckBox} from '@ion/dls/src/components/buttons/';
 *
 *    <FormCheckBox onChange={onCheckBoxChange}
 *      name={loading}
 *      disabled={loading} />
 *
 * ```
 *
 * @param {boolean}  onSave callback which is invoked when the user clicks save.
 * @param {boolean}  loading When true, the loading indicator will be displayed.
 * @param {boolean}  disabled When true, the button will be disabled.
 */
export const FormCheckBox = ({
  formItemStyle,
  useWrapperStyle = true,
  ...props
}: FormCheckBoxProps) => {
  const classes = useStyles();
  return (
    <FormItem fullWidth>
      <div className={useWrapperStyle ? classes.checkboxWrapper : null}
        {...(isObject(formItemStyle) && {
          style : formItemStyle
        })}>
        <CheckBox {...props} />
      </div>
    </FormItem>
  );
};

export default FormCheckBox;
